// Admin API Service

import {
  DashboardData,
  MessagesResponse,
  MessagesFilters,
  AnalyticsData,
  AdminSettings,
  WhatsAppStatus,
  ApiResponse,
} from '../../types/admin';
import { authApi } from './authApi';

// In production, use relative URLs (same domain). In dev, fallback to localhost.
const API_BASE = process.env.NODE_ENV === 'production'
  ? ''
  : (process.env.REACT_APP_API_URL || 'http://localhost:8000');

class AdminApiService {
  private baseUrl = `${API_BASE}/api/admin`;

  private async fetchWithAuth<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = authApi.getToken();

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...options.headers,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        authApi.clearToken();
        window.location.href = '/admin/login';
        throw new Error('Session expired');
      }
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }

    return response.json();
  }

  // Dashboard
  async getDashboard(): Promise<DashboardData> {
    return this.fetchWithAuth<DashboardData>('/dashboard');
  }

  // Messages
  async getMessages(
    page: number = 1,
    pageSize: number = 25,
    filters?: MessagesFilters
  ): Promise<MessagesResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: pageSize.toString(),
    });

    if (filters?.search) params.append('search', filters.search);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.startDate) params.append('start_date', filters.startDate);
    if (filters?.endDate) params.append('end_date', filters.endDate);

    return this.fetchWithAuth<MessagesResponse>(`/messages?${params}`);
  }

  // Analytics
  async getAnalytics(days: number = 7): Promise<AnalyticsData> {
    return this.fetchWithAuth<AnalyticsData>(`/analytics?days=${days}`);
  }

  // Settings
  async getSettings(): Promise<AdminSettings> {
    return this.fetchWithAuth<AdminSettings>('/settings');
  }

  async saveSettings(settings: AdminSettings): Promise<ApiResponse<AdminSettings>> {
    return this.fetchWithAuth<ApiResponse<AdminSettings>>('/settings', {
      method: 'POST',
      body: JSON.stringify(settings),
    });
  }

  // Export
  async exportData(format: 'csv' | 'json' = 'csv'): Promise<Blob> {
    const token = authApi.getToken();
    const response = await fetch(`${this.baseUrl}/export?format=${format}`, {
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    return response.blob();
  }

  // WhatsApp Connection Management (apiwts.top integration)
  async getWhatsAppStatus(): Promise<WhatsAppStatus> {
    return this.fetchWithAuth<WhatsAppStatus>('/whatsapp/connection/status');
  }

  async reconnectWhatsApp(): Promise<ApiResponse<void>> {
    return this.fetchWithAuth<ApiResponse<void>>('/whatsapp/connection/reconnect', {
      method: 'POST',
    });
  }

  async connectWhatsApp(): Promise<ApiResponse<void>> {
    return this.fetchWithAuth<ApiResponse<void>>('/whatsapp/connection/connect', {
      method: 'POST',
    });
  }

  async disconnectWhatsApp(): Promise<ApiResponse<void>> {
    return this.fetchWithAuth<ApiResponse<void>>('/whatsapp/connection/disconnect', {
      method: 'POST',
    });
  }

  async deleteWhatsAppSession(): Promise<ApiResponse<void>> {
    return this.fetchWithAuth<ApiResponse<void>>('/whatsapp/connection/session', {
      method: 'DELETE',
    });
  }

  async getWhatsAppHealth(): Promise<any> {
    return this.fetchWithAuth<any>('/whatsapp/connection/health');
  }
}

export const adminApi = new AdminApiService();
export default adminApi;
